import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, CheckCircle2, KeyRound, Loader2, MailWarning } from "lucide-react";
import { Button } from "components/ui/button";
import { Label } from "components/ui/label";
import { PasswordInput } from "components/ui/password-input";
import { api, isSuccessResponse } from "lib/api";
import { apiURl } from "store/actions";
import {
  validateForm as runFormValidation,
  validatePassword,
  validateConfirmPassword,
} from "lib/validation";
import swtAlert from "lib/sweetAlert";

const formRowClass = "grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4 items-start";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [doneMessage, setDoneMessage] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateAll = () => {
    const newErrors = runFormValidation(formData, {
      password: (v) => validatePassword(v),
      confirmPassword: (v) => validateConfirmPassword(v, formData.password),
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) return;
    if (!validateAll()) return;

    setIsLoading(true);
    try {
      const resp = await api.post(
        apiURl.resetPassword,
        {
          token,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
        },
        { auth: false }
      );

      if (!isSuccessResponse(resp)) {
        const msg = resp?.message || "Couldn't reset password";
        setErrors((prev) => ({ ...prev, form: msg }));
        swtAlert.error("Password Reset Failed", msg);
        return;
      }

      const successMsg =
        resp?.message || "Password has been reset successfully.";
      setDoneMessage(successMsg);
      setIsDone(true);
      swtAlert.success("Password Reset Complete", successMsg);
    } catch (err) {
      const msg = err?.message || "Couldn't reset password";
      setErrors((prev) => ({ ...prev, form: msg }));
      swtAlert.error("Password Reset Failed", msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Reset password | Fortify</title>
      </Helmet>

      <div className="container mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-12 xl:px-16">
        <ResetBody
          token={token}
          isDone={isDone}
          doneMessage={doneMessage}
          onContinue={() => navigate("/sign-in")}
          formData={formData}
          errors={errors}
          isLoading={isLoading}
          onInputChange={handleInputChange}
          onSubmit={handleSubmit}
        />

        <p className="mt-8 text-center text-sm text-muted-foreground">
          <Link
            to="/sign-in"
            className="inline-flex items-center gap-1 font-medium text-accent hover:underline"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to sign in
          </Link>
        </p>
      </div>
    </>
  );
}

/* eslint-disable react/prop-types -- local presentational helpers. */
function ResetBody({
  token,
  isDone,
  doneMessage,
  onContinue,
  formData,
  errors,
  isLoading,
  onInputChange,
  onSubmit,
}) {
  if (!token) return <MissingTokenPanel />;
  if (isDone) {
    return <ResetSuccessPanel message={doneMessage} onContinue={onContinue} />;
  }
  return (
    <ResetForm
      formData={formData}
      errors={errors}
      isLoading={isLoading}
      onInputChange={onInputChange}
      onSubmit={onSubmit}
    />
  );
}

function ResetForm({ formData, errors, isLoading, onInputChange, onSubmit }) {
  return (
    <>
      <h1 className="mb-2 text-center text-2xl font-semibold">
        Reset Your Password
      </h1>
      <p className="mb-8 text-center text-sm text-muted-foreground">
        Choose a new password for your account. Make sure it&apos;s something
        you can remember and keep secure.
      </p>

      <form onSubmit={onSubmit} className="space-y-6">
        <div className={formRowClass}>
          <Label htmlFor="password" className="md:pt-3 md:text-right">
            New Password
          </Label>
          <div>
            <PasswordInput
              id="password"
              name="password"
              placeholder="Enter new password"
              value={formData.password}
              onChange={onInputChange}
              aria-invalid={!!errors.password}
              disabled={isLoading}
              autoComplete="new-password"
            />
            <PasswordHint error={errors.password} />
          </div>
        </div>

        <div className={formRowClass}>
          <Label htmlFor="confirmPassword" className="md:pt-3 md:text-right">
            Confirm Password
          </Label>
          <div>
            <PasswordInput
              id="confirmPassword"
              name="confirmPassword"
              placeholder="Re-enter new password"
              value={formData.confirmPassword}
              onChange={onInputChange}
              aria-invalid={!!errors.confirmPassword}
              disabled={isLoading}
              autoComplete="new-password"
            />
            {errors.confirmPassword && (
              <p className="mt-1 text-xs text-destructive">
                {errors.confirmPassword}
              </p>
            )}
          </div>
        </div>

        {errors.form && (
          <div className={formRowClass}>
            <div />
            <p className="text-sm text-destructive">{errors.form}</p>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 pt-4 md:grid-cols-[260px_1fr]">
          <div />
          <Button
            type="submit"
            disabled={isLoading}
            className="w-full md:w-auto"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Resetting...
              </>
            ) : (
              <>
                <KeyRound className="mr-2 h-4 w-4" />
                Reset Password
              </>
            )}
          </Button>
        </div>
      </form>
    </>
  );
}

function PasswordHint({ error }) {
  if (error) {
    return <p className="mt-1 text-xs text-destructive">{error}</p>;
  }
  return (
    <p className="mt-1 text-xs text-muted-foreground">
      Min 8 characters with upper, lower, number and special character.
    </p>
  );
}

function ResetSuccessPanel({ message, onContinue }) {
  return (
    <div className="space-y-4 rounded-md border border-emerald-200 bg-emerald-50 p-6 text-center text-sm text-emerald-800 dark:border-emerald-900/50 dark:bg-emerald-950/40 dark:text-emerald-200">
      <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
      <h2 className="text-base font-semibold">Password Reset Complete</h2>
      <p>{message}</p>
      <div className="flex justify-center pt-2">
        <Button type="button" size="sm" onClick={onContinue}>
          Continue to Sign In
        </Button>
      </div>
    </div>
  );
}

function MissingTokenPanel() {
  return (
    <div className="space-y-4 rounded-md border border-amber-200 bg-amber-50 p-6 text-center text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200">
      <MailWarning className="mx-auto h-10 w-10 text-amber-500" />
      <h2 className="text-base font-semibold">Reset Link Missing</h2>
      <p>
        This page expects a reset token in the URL. Please open the link from
        the password reset email we sent you.
      </p>
      <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
        <Button asChild size="sm">
          <Link to="/forgot-password">Request a New Link</Link>
        </Button>
        <Button asChild variant="outline" size="sm">
          <Link to="/sign-in">Back to Sign In</Link>
        </Button>
      </div>
    </div>
  );
}
/* eslint-enable react/prop-types */
